import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { UserPicker } from '../shared/UserPicker';
import type { PickingList, AssignPickerInput, WmsUser } from '../../types';

interface AssignPickerModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: AssignPickerInput) => Promise<void>;
  pickingList?: PickingList | null;
  isLoading?: boolean;
}

function formatUserName(user: WmsUser): string {
  if (user.firstname || user.lastname) {
    return [user.firstname, user.lastname].filter(Boolean).join(' ');
  }
  return user.username || user.email;
}

export function AssignPickerModal({
  open,
  onClose,
  onSubmit,
  pickingList,
  isLoading,
}: AssignPickerModalProps) {
  const [selectedUser, setSelectedUser] = useState<WmsUser | null>(null);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      setSelectedUser(null);
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;

    try {
      await onSubmit({
        user_id: selectedUser._id,
        user_name: formatUserName(selectedUser),
      });
      toast.success('Picker assigned successfully');
      setSelectedUser(null);
      onClose();
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Unknown error occurred';
      toast.error(`Failed to assign picker: ${message}`);
      console.error('Error assigning picker:', error);
      // Keep form open for retry
    }
  };

  const handleClose = () => {
    setSelectedUser(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Assign Picker</DialogTitle>
          <DialogDescription>
            Select a user to assign to this picking list.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {pickingList && (
              <div className="text-sm text-muted-foreground">
                Assigning picker to <span className="font-mono font-medium">{pickingList.picking_number}</span>
              </div>
            )}
            <div className="space-y-2">
              <Label>Picker</Label>
              <UserPicker
                value={selectedUser}
                onChange={setSelectedUser}
                placeholder="Search for picker..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!selectedUser || isLoading}>
              {isLoading ? 'Assigning...' : 'Assign'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
